import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';
import { DateTime } from 'luxon';
import {
  useSaveDailyReportMutation,
  SaveDailyReport_Input,
  useDailyReportFormQuery,
  DailyReportTableQuery,
  SaveDailyReportMutation,
  DailyReportTableDocument
} from 'generated/graphql';
import _ from 'lodash';

export type IFormState = {
  description: string;
  id: string;
  projectId: string;
  reportDate: string;
};

const initialState = {
  description: '',
  id: '',
  projectId: '',
  reportDate: DateTime.now().toFormat('yyyy-LL-dd')
};

export function useFormState(id: string, projectId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      description: {
        label: 'Description',
        type: 'text'
      },
      id: {
        label: 'Id',
        type: 'text'
      },
      projectId: {
        label: 'Project id',
        type: 'text'
      },
      reportDate: {
        label: 'Report date',
        type: 'date'
      }
    }
  });

  useEffect(() => {
    if (formState.projectId === '' && projectId !== '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'projectId',
          value: projectId
        }
      });
    }

    return () => {};
  }, [
    dispatch,
    formState.projectId,
    projectId
  ]);


  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (formState.id === '' && data.id !== '' && !loading && called) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveDailyReport() {
  const [
    fnMutate,
    tuple
  ] = useSaveDailyReportMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveDailyReport_Input) => {
      const data = await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<DailyReportTableQuery, SaveDailyReportMutation>(DailyReportTableDocument, (prevState, { mutationResult }) => {
            if (!prevState.dailyReports || !mutationResult.data?.saveDailyReport?.id) {
              return prevState;
            }
            const newReport = input.id !== mutationResult.data.saveDailyReport.id;
            if (newReport) {
              return {
                ...prevState,
                dailyReports: _.compact([
                  ...prevState.dailyReports,
                  mutationResult.data.saveDailyReport
                ])
              };
            } else {
              const updatedReports = prevState.dailyReports.map((node) => {
                if (node.id === mutationResult.data?.saveDailyReport?.id) {
                  return {
                    ...node,
                    ...mutationResult.data.saveDailyReport
                  };
                }
                return node;
              });
              return {
                ...prevState,
                dailyReports: updatedReports
              };
            }
          })
        }
      });
      return data;
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: {
          id: tuple.data?.saveDailyReport?.id ?? ''
        }
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}


export interface IData extends IFormState {}

export function useQuery(id: string) {
  const tuple = useDailyReportFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.dailyReport?.id ?? '',
        reportDate: tuple.data?.dailyReport?.reportDate.toFormat('yyyy-LL-dd') ?? '',
        description: tuple.data?.dailyReport?.description ?? '',
        projectId: tuple.data?.dailyReport?.project.id ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
